import { ArrowRight } from "lucide-react";

const KPIS = [
  { label: "Tail spend", value: "$9.8M", sub: "20.2% of total", color: "#f59e0b", accent: false },
  { label: "Tail suppliers", value: "12 of 312", sub: "lowest-spend long tail", color: "#7c3aed", accent: false },
  { label: "Fragmented spend", value: "$11.2M", sub: "1:1 product-to-order", color: "#ec1c8c", accent: true },
  { label: "Consolidation value", value: "$1.46M", sub: "catalog + panel migration", color: "#10b981", accent: false },
];

const FRAGMENTED = [
  { category: "Electrical components", orders: "1,249", spend: "$1.99M", ratio: "1.00" },
  { category: "Fasteners & hardware", orders: "1,025", spend: "$1.92M", ratio: "1.00" },
  { category: "Power sources", orders: "905", spend: "$5.71M", ratio: "1.00" },
  { category: "Heating & ventilation", orders: "807", spend: "$0.44M", ratio: "1.00" },
  { category: "Gaskets", orders: "746", spend: "$0.49M", ratio: "1.00" },
  { category: "Transportation components", orders: "596", spend: "$1.24M", ratio: "1.00" },
];

const SPLIT = [
  { label: "Tail · $9.8M", pct: 20.2, color: "#ec1c8c" },
  { label: "Core · $38.5M", pct: 79.8, color: "#1e1b4b" },
];

const TAIL_SUPPLIERS: [string, number, string][] = [
  ["SKF", 2.4, "$2.4M"],
  ["Bosch", 2.1, "$2.1M"],
  ["Lear", 1.8, "$1.8M"],
  ["Mann+Hummel", 1.5, "$1.5M"],
  ["Hutchinson", 1.2, "$1.2M"],
  ["NSK", 0.963, "$963K"],
];

function TailCoreDonut() {
  const r = 62;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg className="sd-donut sd-donut--lg" viewBox="0 0 160 160" role="img" aria-label="Tail versus core spend">
      {SPLIT.map(({ label, pct, color }) => {
        const dash = (pct / 100) * circ;
        const rot = (offset / 100) * 360 - 90;
        offset += pct;
        return (
          <circle
            key={label}
            cx="80"
            cy="80"
            r={r}
            fill="none"
            stroke={color}
            strokeWidth="22"
            strokeDasharray={`${dash} ${circ - dash}`}
            transform={`rotate(${rot} 80 80)`}
          />
        );
      })}
      <text x="80" y="76" textAnchor="middle" fontSize="9" fill="#9ca3af">Tail</text>
      <text x="80" y="95" textAnchor="middle" fontSize="16" fontWeight="700" fill="#1e1b4b">20.2%</text>
    </svg>
  );
}

export function TailFragmentationPanel() {
  const max = TAIL_SUPPLIERS[0][1];

  return (
    <div className="sm-panel">
      <div className="sm-kpi-grid">
        {KPIS.map(({ label, value, sub, color, accent }) => (
          <div key={label} className="sm-kpi" style={{ borderColor: color }}>
            <p className="sm-kpi__label">{label}</p>
            <p className={`sm-kpi__value${accent ? " sm-kpi__value--accent" : ""}`}>{value}</p>
            <p className="sm-kpi__sub">{sub}</p>
          </div>
        ))}
      </div>

      <div className="sd-split">
        <div className="si-card sm-card sm-card--flush">
          <div className="sm-card__head sm-card__head--stacked">
            <div>
              <p className="sm-card__title">Fragmented Categories</p>
              <p className="sm-card__subtitle">High order count, near 1:1 unique-product ratio, no standardization</p>
            </div>
            <span className="sm-tag">Standardization gap</span>
          </div>
          <table className="sm-table">
            <thead>
              <tr>
                <th>Category</th>
                <th className="sm-table__right">Orders</th>
                <th className="sm-table__right">Spend</th>
                <th className="sm-table__right">Frag. Ratio</th>
              </tr>
            </thead>
            <tbody>
              {FRAGMENTED.map(({ category, orders, spend, ratio }) => (
                <tr key={category}>
                  <td className="sm-table__strong">{category}</td>
                  <td className="sm-table__right op-purple">{orders}</td>
                  <td className="sm-table__right">{spend}</td>
                  <td className="sm-table__right sm-table__accent sm-table__strong">{ratio}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="sm-table-note">
            A 1.00 ratio means every order is a unique product spec, the signature of catalog absence.
          </p>
        </div>

        <div className="si-card sm-card">
          <p className="sm-card__title">Tail vs Core</p>
          <div className="sd-tail-donut">
            <TailCoreDonut />
          </div>
          <div className="sd-geo__legend">
            {SPLIT.map(({ label, pct, color }) => (
              <div key={label} className="sd-geo__item">
                <span className="sd-geo__swatch" style={{ background: color }} />
                <span className="sd-geo__label">{label}</span>
                <strong>{pct}%</strong>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="si-card sm-card">
        <div className="sm-card__head sm-card__head--stacked sm-card__head--flat">
          <div>
            <p className="sm-card__title sm-card__title--tight">Tail Suppliers</p>
            <p className="sm-card__subtitle">Lowest-spend named suppliers · catalog &amp; panel migration targets</p>
          </div>
          <span className="sm-tag">6 of 12</span>
        </div>
        <div className="sd-bars">
          {TAIL_SUPPLIERS.map(([name, v, label]) => (
            <div key={name} className="sd-bar">
              <button type="button" className="sd-bar__label sd-bar__label--wide">{name}</button>
              <div className="sm-geo__track">
                <div className="sm-geo__fill" style={{ width: `${(v / max) * 100}%` }} />
              </div>
              <p className="sd-bar__value">{label}</p>
            </div>
          ))}
        </div>
        <p className="sm-chart__note sd-note">
          These reputable Tier-1s sit at the bottom of the spend curve. Shift their micro-buys into existing{" "}
          <span className="op-purple">panels and catalogs</span>, and deploy p-cards to cut transaction cost.
        </p>
      </div>

      <div className="si-card sm-card sm-card--flush sd-callout-card">
        <div className="sm-callout">
          <div className="sm-callout__body">
            <p className="sm-callout__eyebrow">From: Tail &amp; Fragmentation</p>
            <p className="sm-callout__text">
              <strong>12 tail suppliers carry $9.75M across fragmented buys, and $11.2M sits at a 1:1 product-to-order ratio.</strong>{" "}
              Many are reputable Tier-1s; shift into catalogs and panels, deploy p-cards for micro-buys. Consolidation unlocks
              ~$1.46M.
            </p>
          </div>
          <button type="button" className="sm-callout__btn">
            Send to Opportunity
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
